import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Users,
  UserRound,
  ChevronDown,
  User,
  LogOut,
} from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { useAuth, api } from "@/context/AuthContext";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { useQueueCount } from "@/hooks/useQueueCount";
import { FeedbackModal } from "@/components/FeedbackModal";
import { NotifyMeButton } from "@/components/NotifyMeButton";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/* Screen 4 — Homepage (approved SalesPit design). */

export default function Homepage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const repsAvailable = useQueueCount();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Ask the backend whether this user should see the post-session feedback
  // modal. The check itself is idempotent and includes the "dismissed at
  // most twice + at least one completed session" rule the spec requires.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/feedback/should-show");
        if (alive && r.data?.show) setFeedbackOpen(true);
      } catch {
        // silently no-op — never block homepage on this
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  const firstName = user?.name?.split(" ")[0] || "there";
  const initial = (user?.name || "U").charAt(0).toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const Avatar = ({ size = "h-9 w-9" }) =>
    user?.picture ? (
      <img
        src={user.picture}
        alt={user.name}
        className={`${size} rounded-full object-cover`}
      />
    ) : (
      <span
        className={`${size} flex items-center justify-center rounded-full bg-[#E52525] text-sm font-bold text-white`}
      >
        {initial}
      </span>
    );

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] font-body text-white">
      <DashboardSidebar active="home" />

      {/* Main */}
      <main
        className="flex-1 px-6 py-7 md:px-12 md:py-10"
        data-testid="homepage"
      >
        {/* mobile logo bar */}
        <div className="mb-8 flex items-center justify-between md:hidden">
          <Logo className="text-2xl" />
        </div>

        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">
                Welcome back, {firstName} 👋
              </h1>
              <p className="mt-2 text-base text-zinc-400">
                Ready to level up your sales skills?
              </p>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="profile-menu-btn"
                  className="flex items-center gap-2 rounded-full border border-white/10 bg-[#141414] p-1 pr-2 transition-colors duration-200 hover:border-white/25"
                >
                  <Avatar />
                  <ChevronDown size={16} className="text-zinc-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-44 border-white/10 bg-[#141414] text-white"
              >
                <DropdownMenuItem
                  data-testid="menu-profile-btn"
                  onClick={() => navigate("/profile")}
                  className="cursor-pointer focus:bg-white/10 focus:text-white"
                >
                  <User size={16} className="mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="menu-logout-btn"
                  onClick={handleLogout}
                  className="cursor-pointer focus:bg-white/10 focus:text-white"
                >
                  <LogOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Action cards */}
          <div className="mt-10 space-y-5">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="enter-the-pit-card"
              onClick={() => navigate("/practice-setup")}
              className="group flex w-full items-center gap-5 rounded-3xl bg-[#E52525] p-7 text-left shadow-[0_0_40px_-8px_rgba(229,37,37,0.5)] transition-[background-color] duration-300 hover:bg-[#FF3333]"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white">
                <UserRound size={26} />
              </span>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold tracking-wide text-white">
                  ENTER THE PIT
                </h2>
                <p className="mt-1 text-sm text-white/80">
                  Get matched with another sales rep and start practising.
                </p>
              </div>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-transform duration-300 group-hover:translate-x-1">
                <ArrowRight size={20} />
              </span>
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              data-testid="practice-with-friend-card"
              onClick={() => navigate("/practice")}
              className="group flex w-full items-center gap-5 rounded-3xl border border-white/10 bg-[#141414] p-7 text-left transition-colors duration-300 hover:border-white/20"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/5 text-zinc-300">
                <Users size={26} />
              </span>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold tracking-wide text-white">
                  PRACTICE WITH FRIEND
                </h2>
                <p className="mt-1 text-sm text-zinc-400">
                  Invite a friend and practise together.
                </p>
              </div>
            </motion.button>
          </div>

          <div
            className="mt-9 flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-white/5 pt-6 text-sm text-zinc-500"
            data-testid="reps-available-homepage"
          >
            {repsAvailable === null ? (
              <span>Checking who's in the pit…</span>
            ) : repsAvailable === 0 ? (
              <>
                <span className="font-medium text-zinc-300">
                  No reps available right now. Be the first in the pit!
                </span>
                <NotifyMeButton
                  variant="ghost"
                  returnTo="/homepage"
                  testid="home-notify-me-btn"
                />
              </>
            ) : (
              <>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#22C55E]" />
                  <span className="font-medium text-zinc-300">Reps Available</span>
                </span>
                <span className="text-zinc-700">·</span>
                <span data-testid="reps-available-count">
                  {repsAvailable} {repsAvailable === 1 ? "rep" : "reps"} in the pit
                </span>
              </>
            )}
          </div>
        </div>
      </main>

      <FeedbackModal
        open={feedbackOpen}
        onSubmitted={() => setFeedbackOpen(false)}
        onDismissed={() => setFeedbackOpen(false)}
      />
    </div>
  );
}
